import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Form, FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  useCreateWarrantyTypeMutation,
  useGetWarrantyTypeQuery,
  useUpdateWarrantyTypeMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {WarrantyTypeForm} from './components/WarrantyTypeForm';
import {WarrantyTypeFormSchema, WarrantyTypeFormType} from './components/WarrantyTypeFormSchema';

export function WarrantyTypesDetail() {
  const {id} = useParams();
  const navigate = useNavigate();

  const {data, isLoading, isError} = useGetWarrantyTypeQuery(
    isNotNil(id) ? {warrantyTypeId: id} : skipToken
  );

  const [createWarrantyType] = useCreateWarrantyTypeMutation();
  const [updateWarrantyType] = useUpdateWarrantyTypeMutation();

  const handleSubmit: FormSubmitHandler<WarrantyTypeFormType> = async (
    formData,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      warrantyType: formData.warrantyType,
      source: formData.source,
      durationMonths: formData.durationMonths,
      durationKm: formData.durationKm,
      description: formData.description,
      externalId: formData.externalId,
    };

    const request = isNil(id)
      ? createWarrantyType({createWarrantyTypeRequestBody: body}).unwrap()
      : updateWarrantyType({warrantyTypeId: id, updateWarrantyTypeRequestBody: body}).unwrap();

    await request
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.warrantyTypes);
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    warrantyType: data?.warrantyType,
    source: data?.source,
    durationMonths: data?.durationMonths ?? null,
    durationKm: data?.durationKm ?? null,
    description: data?.description ?? '',
    externalId: data?.externalId ?? null,
  };

  return (
    <>
      <Helmet
        title={i18n.t(
          isNil(id)
            ? 'entity.warrantyType.actions.new'
            : 'page.vehicleSettings.labels.warrantyTypes'
        )}
      />
      <SettingsTemplate
        header={{
          title: isNil(id)
            ? i18n.t('entity.warrantyType.actions.new')
            : (data?.description ?? i18n.t('page.vehicleSettings.labels.warrantyTypes')),
          breadcrumbs: [
            {
              label: i18n.t('page.vehicleSettings.labels.warrantyTypes'),
              href: settingsRoutes.warrantyTypes,
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        isCreating={isNil(id)}
        data-testid={testIds.settings.warrantyTypesDetail('template')}
      >
        <Form<WarrantyTypeFormType>
          defaultValues={defaultValues}
          experimentalZodSchema={WarrantyTypeFormSchema}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <>
              <WarrantyTypeForm control={control} />
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.warrantyTypes),
                  },
                  {
                    control,
                    type: 'form-button',
                    buttonType: 'submit',
                    title: isNil(id)
                      ? i18n.t('general.labels.createNew')
                      : i18n.t('general.actions.saveChanges'),
                  },
                ]}
                data-testid={testIds.settings.warrantyTypesDetail('footer')}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
